import React, { FC, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';

interface DateTimePickerModalProps {
  isOpen: boolean;
  initialDate: Date;
  onClose: () => void;
  onSelect: (d: Date) => void;
  label?: string; // optional header override
}

const monthNamesRu = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
const weekdayNamesRu = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];

const pad = (n: number) => String(n).padStart(2,'0');

export const DateTimePickerModal: FC<DateTimePickerModalProps> = ({ isOpen, initialDate, onClose, onSelect, label }) => {
  const [viewYear, setViewYear] = useState(initialDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialDate.getMonth());
  const [selectedDay, setSelectedDay] = useState(initialDate.getDate());
  const [hours, setHours] = useState(initialDate.getHours());
  const [minutes, setMinutes] = useState(initialDate.getMinutes());

  const daysMatrix = useMemo(() => {
    // Always render 6 weeks (42 cells), Monday-first. Fill adjacent months' dates.
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const startWeekday = (firstOfMonth.getDay() + 6) % 7; // Monday=0
    const firstCellDate = new Date(viewYear, viewMonth, 1 - startWeekday);
    const cells: Array<{ y: number; m: number; d: number; inMonth: boolean }>=[];
    for (let i = 0; i < 42; i++) {
      const dt = new Date(firstCellDate);
      dt.setDate(firstCellDate.getDate() + i);
      cells.push({ y: dt.getFullYear(), m: dt.getMonth(), d: dt.getDate(), inMonth: dt.getMonth() === viewMonth });
    }
    const rows: Array<Array<{ y:number; m:number; d:number; inMonth:boolean }>> = [];
    for (let i=0;i<cells.length;i+=7) rows.push(cells.slice(i,i+7));
    return rows;
  }, [viewYear, viewMonth]);

  const apply = () => {
    const date = new Date(viewYear, viewMonth, selectedDay, hours, minutes, 0, 0);
    onSelect(date);
    onClose();
  };

  const moveMonth = (delta: number) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setViewMonth(m); setViewYear(y); // keep selectedDay if still valid else clamp
    const maxDay = new Date(y, m+1, 0).getDate();
    if (selectedDay > maxDay) setSelectedDay(maxDay);
  };

  if (!isOpen) return null;

  return createPortal(
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e=>e.stopPropagation()}>
        {/* Header month navigation */}
        <div style={styles.headerRow}>          
          <button style={styles.navBtn} onClick={()=>moveMonth(-1)}>&lt;</button>
          <div style={styles.monthLabel}>{monthNamesRu[viewMonth]} {viewYear}</div>
          <button style={styles.navBtn} onClick={()=>moveMonth(1)}>&gt;</button>
        </div>
        {/* Weekday labels */}
        <div style={styles.weekdaysRow}>
          {weekdayNamesRu.map(w=> <div key={w} style={styles.weekdayCell}>{w}</div>)}
        </div>
        {/* Days grid */}
        <div style={styles.daysGrid}>
          {daysMatrix.map((row,ri)=>(
            <div key={ri} style={styles.daysRow}>
              {row.map((cell,ci)=>{
                const isSelected = cell.d === selectedDay && cell.m === viewMonth && cell.y === viewYear;
                return (
                  <div
                    key={ci}
                    onClick={()=> { setViewYear(cell.y); setViewMonth(cell.m); setSelectedDay(cell.d); }}
                    style={{
                      ...styles.dayCell,
                      cursor: 'pointer',
                      background: isSelected? '#2D63FF':'#2E3749',
                      color: isSelected? '#fff' : (cell.inMonth? '#D4DAE3':'#8A93A8'),
                      boxShadow: isSelected? '0 0 0 2px rgba(255,255,255,0.25)':'none',
                      border: isSelected? '1px solid #2D63FF':'1px solid #2E3749'
                    }}
                  >{pad(cell.d)}</div>
                );
              })}
            </div>
          ))}
        </div>
        {/* Time selection */}
        <div style={styles.timeSectionTitle}>Время</div>
        <div style={styles.timeRow}>
          <input
            type="number"
            min={0}
            max={23}
            value={hours}
            onChange={e=>{
              let v = Number(e.target.value); if (!Number.isFinite(v)) v = 0; v = Math.max(0, Math.min(23, v)); setHours(v);
            }}
            style={styles.timeInput}
          />
          <div style={{ color:'#888', fontSize:28, fontWeight:600 }}>:</div>
          <input
            type="number"
            min={0}
            max={59}
            value={minutes}
            onChange={e=>{
              let v = Number(e.target.value); if (!Number.isFinite(v)) v = 0; v = Math.max(0, Math.min(59, v)); setMinutes(v);
            }}
            style={styles.timeInput}
          />
        </div>
        {/* Actions */}
        <div style={styles.actionsRow}>
          <button style={styles.cancelBtn} onClick={onClose}>Отмена</button>
          <button style={styles.selectBtn} onClick={apply}>Выбрать дату</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  overlay: {
    position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:3000,
  },
  modal: {
    width:320, background:'#1F2534', border:'1px solid #2E3749', borderRadius:16, padding:'16px 16px 20px',
    boxShadow:'0 2px 16px #0000003D', display:'flex', flexDirection:'column', fontFamily:'inherit'
  },
  headerRow: {
    display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12
  },
  navBtn: {
    background:'#1F2534', color:'#fff', border:'1px solid #2E3749', borderRadius:8, width:36, height:36, cursor:'pointer', fontSize:18, fontWeight:600
  },
  monthLabel: {
    flex:1, textAlign:'center', fontSize:18, fontWeight:700, color:'#fff'
  },
  weekdaysRow: { display:'flex', gap:6, marginBottom:6 },
  weekdayCell: { flex:1, textAlign:'center', fontSize:12, color:'#9AA4B2' },
  daysGrid: { display:'flex', flexDirection:'column', gap:6, marginBottom:16 },
  daysRow: { display:'flex', gap:6 },
  dayCell: {
    flex:1, height:36, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, userSelect:'none',
    background:'#2E3749', border:'1px solid #2E3749', color:'#D4DAE3', transition:'background .15s,border-color .15s, box-shadow .15s'
  },
  timeSectionTitle: { marginTop:8, marginBottom:12, fontSize:16, fontWeight:700, color:'#fff', textAlign:'center' },
  timeRow: { display:'flex', justifyContent:'center', alignItems:'center', gap:12 },
  timeInput: {
    width:56, height:48, background:'#2E3749', border:'1px solid #2E3749', borderRadius:8, color:'#fff', fontSize:22, fontWeight:700, textAlign:'center'
  },
  actionsRow: { marginTop:20, display:'flex', gap:12 },
  cancelBtn: {
    flex:1, background:'#424F67', color:'#fff', border:'1px solid #2E3749', borderRadius:10, padding:'12px 0', cursor:'pointer', fontSize:14, fontWeight:600,
    boxShadow:'0 2px 8px rgba(0,0,0,0.25)'
  },
  selectBtn: {
    flex:1, background:'#2D63FF', color:'#fff', border:'1px solid #2D63FF', borderRadius:10, padding:'12px 0', cursor:'pointer', fontSize:14, fontWeight:700,
    boxShadow:'0 0 0 2px rgba(255,255,255,0.25)'
  }
};

export default DateTimePickerModal;
